import { NavLink } from "react-router";
import type { ReactNode } from "react";
import { useAuth, useLogout } from "../../hooks/useAuth";
import { usePendingCount } from "../../hooks/usePosts";

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  badgeKey?: "pending";
}

const NAV: NavItem[] = [
  { to: "/", label: "Dashboard", icon: <IconGrid /> },
  { to: "/generate", label: "Generieren", icon: <IconSparkles /> },
  { to: "/review", label: "Review", icon: <IconCheck />, badgeKey: "pending" },
  { to: "/schedule", label: "Plan", icon: <IconCalendar /> },
  { to: "/config", label: "Brand", icon: <IconSliders /> },
  { to: "/analytics", label: "Analytics", icon: <IconChart /> },
  { to: "/settings", label: "Einstellungen", icon: <IconGear /> },
];

export function Sidebar() {
  const { data: pendingCount } = usePendingCount();
  const { data: user } = useAuth();
  const logout = useLogout();

  return (
    <aside className="flex w-60 flex-col gap-1 bg-brand-text-dark/95 px-4 py-6 text-brand-cream">
      <div className="mb-8 px-2">
        <div className="headline text-5xl">TRULY</div>
        <div className="mt-1 text-xs tracking-widest uppercase opacity-60">
          Cockpit
        </div>
      </div>
      <nav className="flex flex-1 flex-col gap-1">
        {NAV.map((item) => {
          const count = item.badgeKey === "pending" ? pendingCount : undefined;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-brand-blue text-brand-cream"
                    : "text-brand-cream/70 hover:bg-white/5 hover:text-brand-cream"
                }`
              }
            >
              <span className="flex h-5 w-5 items-center justify-center">
                {item.icon}
              </span>
              <span className="flex-1">{item.label}</span>
              {count != null && count > 0 ? (
                <span
                  className="rounded-full bg-brand-magenta px-2 py-0.5 text-[10px] font-bold tabular-nums text-white"
                  title={`${count} Posts warten auf Review`}
                >
                  {count}
                </span>
              ) : null}
            </NavLink>
          );
        })}
      </nav>

      {/* User chip + logout */}
      {user ? (
        <div className="mt-4 border-t border-white/10 pt-4">
          <div className="flex items-center gap-3 px-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-blue text-xs font-bold uppercase text-brand-cream">
              {user.username.slice(0, 2)}
            </div>
            <div className="flex-1 overflow-hidden">
              <div className="truncate text-sm font-semibold">
                {user.username}
              </div>
              <div className="text-[10px] uppercase tracking-wider opacity-50">
                {user.role}
              </div>
            </div>
            <button
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
              title="Abmelden"
              className="rounded-md p-1.5 text-brand-cream/60 transition hover:bg-white/10 hover:text-brand-cream disabled:opacity-50"
            >
              <IconLogout />
            </button>
          </div>
        </div>
      ) : null}
    </aside>
  );
}

function IconLogout() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path
        fillRule="evenodd"
        d="M3 4a1 1 0 011-1h7a1 1 0 110 2H5v10h6a1 1 0 110 2H4a1 1 0 01-1-1V4zm10.3 3.3a1 1 0 011.4 0l3 3a1 1 0 010 1.4l-3 3a1 1 0 11-1.4-1.4L14.6 12H8a1 1 0 110-2h6.6l-1.3-1.3a1 1 0 010-1.4z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function IconGrid() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
      <rect x="2" y="2" width="7" height="7" rx="1" />
      <rect x="11" y="2" width="7" height="7" rx="1" />
      <rect x="2" y="11" width="7" height="7" rx="1" />
      <rect x="11" y="11" width="7" height="7" rx="1" />
    </svg>
  );
}
function IconSparkles() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
      <path d="M10 2l1.5 4.5L16 8l-4.5 1.5L10 14l-1.5-4.5L4 8l4.5-1.5L10 2zm6 10l.9 2.6 2.6.9-2.6.9-.9 2.6-.9-2.6-2.6-.9 2.6-.9.9-2.6z" />
    </svg>
  );
}
function IconCheck() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
      <path d="M16.7 5.3a1 1 0 00-1.4 0L8 12.6 4.7 9.3a1 1 0 10-1.4 1.4l4 4a1 1 0 001.4 0l8-8a1 1 0 000-1.4z" />
    </svg>
  );
}
function IconCalendar() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
      <path d="M6 2a1 1 0 011 1v1h6V3a1 1 0 112 0v1h1a2 2 0 012 2v11a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2h1V3a1 1 0 011-1zm11 6H3v9a1 1 0 001 1h12a1 1 0 001-1V8z" />
    </svg>
  );
}
function IconSliders() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
      <path d="M5 3a1 1 0 011 1v4a2 2 0 110 4v5a1 1 0 11-2 0v-5a2 2 0 110-4V4a1 1 0 011-1zm10 0a1 1 0 011 1v5a2 2 0 110 4v4a1 1 0 11-2 0v-4a2 2 0 110-4V4a1 1 0 011-1z" />
    </svg>
  );
}
function IconChart() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
      <path d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm2-4a1 1 0 011-1h1a1 1 0 011 1v2a1 1 0 01-1 1H6a1 1 0 01-1-1v-2zm5-5a1 1 0 011-1h1a1 1 0 011 1v7a1 1 0 01-1 1h-1a1 1 0 01-1-1V8zm5-4a1 1 0 011-1h1a1 1 0 011 1v11a1 1 0 01-1 1h-1a1 1 0 01-1-1V4z" />
    </svg>
  );
}
function IconGear() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
      <path
        fillRule="evenodd"
        d="M11.5 2a1 1 0 01.95.68l.5 1.5a7 7 0 011.44.6l1.42-.7a1 1 0 011.25.32l1.5 2a1 1 0 01-.2 1.36l-1.16.95a7 7 0 010 1.58l1.16.95a1 1 0 01.2 1.36l-1.5 2a1 1 0 01-1.25.32l-1.42-.7a7 7 0 01-1.44.6l-.5 1.5A1 1 0 0111.5 18h-3a1 1 0 01-.95-.68l-.5-1.5a7 7 0 01-1.44-.6l-1.42.7a1 1 0 01-1.25-.32l-1.5-2a1 1 0 01.2-1.36l1.16-.95a7 7 0 010-1.58l-1.16-.95a1 1 0 01-.2-1.36l1.5-2a1 1 0 011.25-.32l1.42.7a7 7 0 011.44-.6l.5-1.5A1 1 0 018.5 2h3zM10 7a3 3 0 100 6 3 3 0 000-6z"
        clipRule="evenodd"
      />
    </svg>
  );
}
